import { useState } from 'react';
import { ApiError, api, type ChangeSetView, type Intent } from '../api/client';
import { plural } from '../lib/format';
import { ConflictList, Outcome, StepList } from './changesets';
import { Button, Drawer, Empty, Notice } from './ui';

type Phase = { name: 'staged' } | { name: 'planning' } | { name: 'planned'; cs: ChangeSetView } | { name: 'applying'; cs: ChangeSetView } | { name: 'done'; cs: ChangeSetView };

export interface ReviewItem {
  /** Stable key for the staged change. */
  key: string;
  /** Plain-language line shown before the plan is checked. */
  description: string;
  intent: Intent;
}

/**
 * Review staged changes, check them against the phone system (plan), apply, and show the
 * outcome. Shared by every page that changes things, so they all behave the same way.
 */
export function ReviewDrawer({
  open,
  tenant,
  items,
  onUnstage,
  onClose,
  makePlan,
  planned,
  onApplied
}: {
  open: boolean;
  tenant: string;
  items: ReviewItem[];
  onUnstage: (key: string) => void;
  onClose: (applied: boolean) => void;
  /** Plans another way than from the items' intents (a phone template, say). */
  makePlan?: () => Promise<ChangeSetView>;
  /** A plan made already (a seat change, say): the drawer starts at it, and Back discards it and closes. */
  planned?: ChangeSetView;
  /** Called with the applied change set, before the drawer closes. */
  onApplied?: (cs: ChangeSetView) => void;
}) {
  // Fresh state for every opening: the parent changes `key` each time.
  const [phase, setPhase] = useState<Phase>(planned ? { name: 'planned', cs: planned } : { name: 'staged' });
  const [error, setError] = useState<{ message: string; conflicts?: ApiError['conflicts'] } | null>(null);

  // Order matters: additions before their login emails; the planner reorders safely anyway.
  const intents = items.map((i) => i.intent);

  async function plan() {
    setError(null);
    setPhase({ name: 'planning' });
    try {
      const changeSet = makePlan ? await makePlan() : (await api.plan(tenant, intents)).changeSet;
      setPhase({ name: 'planned', cs: changeSet });
    } catch (e) {
      setError({ message: e instanceof Error ? e.message : String(e) });
      setPhase({ name: 'staged' });
    }
  }

  async function apply(cs: ChangeSetView) {
    setError(null);
    setPhase({ name: 'applying', cs });
    try {
      const { changeSet } = await api.apply(cs.id);
      setPhase({ name: 'done', cs: changeSet });
      if (changeSet.status === 'applied') onApplied?.(changeSet);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'STALE') {
        setError({ message: 'Someone changed these after you started. Check the plan again before applying.', conflicts: e.conflicts });
        setPhase({ name: 'staged' });
        return;
      }
      setError({ message: e instanceof Error ? e.message : String(e) });
      setPhase({ name: 'planned', cs });
    }
  }

  async function back(cs: ChangeSetView) {
    if (planned) {
      void api.discard(cs.id).catch(() => undefined);
      onClose(false);
      return;
    }
    setPhase({ name: 'staged' });
    try {
      await api.discard(cs.id);
    } catch {
      /* an unapplied plan left behind is harmless; it shows as waiting in history */
    }
  }

  const close = () => {
    if (phase.name === 'planned') void api.discard(phase.cs.id).catch(() => undefined);
    onClose(phase.name === 'done' && phase.cs.status === 'applied');
  };

  const done = phase.name === 'done' ? phase.cs : null;
  const title = phase.name === 'done' ? (done?.status === 'applied' ? 'Changes applied' : 'Changes not applied') : phase.name === 'planned' || phase.name === 'applying' ? 'Check and apply' : 'Review your changes';

  return (
    <Drawer
      open={open}
      onClose={close}
      title={title}
      subtitle={tenant}
      foot={
        phase.name === 'staged' || phase.name === 'planning' ? (
          <>
            <Button variant="ghost" onClick={close}>
              Keep editing
            </Button>
            <Button variant="primary" disabled={items.length === 0 || phase.name === 'planning'} onClick={plan}>
              {phase.name === 'planning' ? 'Checking…' : 'Check plan'}
            </Button>
          </>
        ) : phase.name === 'planned' || phase.name === 'applying' ? (
          <>
            <Button variant="ghost" className="left" disabled={phase.name === 'applying'} onClick={() => back(phase.cs)}>
              Back
            </Button>
            <Button variant="primary" disabled={phase.name === 'applying'} onClick={() => apply(phase.cs)}>
              {phase.name === 'applying' ? 'Applying…' : `Apply ${plural(phase.cs.steps.length, 'change')}`}
            </Button>
          </>
        ) : (
          <Button variant="primary" onClick={close}>
            Done
          </Button>
        )
      }
    >
      {error && (
        <Notice tone="crit" title="That didn't work">
          {error.message}
          {error.conflicts && error.conflicts.length > 0 && <ConflictList conflicts={error.conflicts} />}
        </Notice>
      )}

      {(phase.name === 'staged' || phase.name === 'planning') && (
        <>
          <span className="muted">Nothing has changed yet. Next, Stitch checks these against your phone system and shows exactly what will happen.</span>
          <ol className="steps">
            {items.map((s, i) => (
              <li className="step" key={s.key}>
                <span className="step-num" aria-hidden="true">
                  {i + 1}
                </span>
                <div className="step-body">
                  <span className="step-title">{s.description}</span>
                  <span>
                    <button type="button" className="link-btn" onClick={() => onUnstage(s.key)}>
                      Remove from changes
                    </button>
                  </span>
                </div>
              </li>
            ))}
          </ol>
          {items.length === 0 && <Empty title="No changes left" />}
        </>
      )}

      {(phase.name === 'planned' || phase.name === 'applying') && (
        <>
          <span className="muted">This is the plan, checked against the phone system just now. Each step is verified as it is applied; if one fails, everything is put back.</span>
          <StepList steps={phase.cs.steps} />
          {phase.cs.notes.length > 0 && (
            <Notice title="Good to know">
              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                {phase.cs.notes.map((n, i) => (
                  <li key={i}>{n}</li>
                ))}
              </ul>
            </Notice>
          )}
        </>
      )}

      {done && (
        <>
          <Outcome cs={done} />
          <StepList steps={done.steps} showState />
        </>
      )}
    </Drawer>
  );
}
